/**
 * Helpers for /risques/extract-risk responses: unwrap arrays and clean text
 * so the import wizard always shows visible titles (no invisible-only names).
 */

const ZWSP = /[\u200B-\u200D\uFEFF]/g

export function sanitizeRiskText(value: unknown): string {
  if (value == null) return ''
  return String(value).replace(ZWSP, '').replace(/\u00a0/g, ' ').trim()
}

export function hasVisibleRiskName(name: string): boolean {
  return sanitizeRiskText(name).length > 0
}

const NAME_KEYS = [
  'RisqueName',
  'name',
  'Name',
  'title',
  'Title',
  'label',
  'Label',
  'risk_name',
  'riskName',
  'RiskName',
  'question',
  'item',
  'summary',
  'text',
  'content',
  'libelle',
  'Libelle',
] as const

export function pickRiskNameFromRecord(risk: Record<string, unknown>): string {
  for (const key of NAME_KEYS) {
    const v = risk[key]
    if (v != null && sanitizeRiskText(v)) return sanitizeRiskText(v)
  }
  const nested = risk.risk ?? risk.Risk
  if (nested && typeof nested === 'object') {
    return pickRiskNameFromRecord(nested as Record<string, unknown>)
  }
  return ''
}

/**
 * Accepts several backend shapes: top-level array, { data }, { risks }, { rows }, nested { data: { risks } }, etc.
 */
export function extractDetectedRisksArray(response: unknown): unknown[] {
  if (response == null) return []

  let data: unknown = response
  if (typeof response === 'string') {
    try {
      data = JSON.parse(response)
    } catch {
      return []
    }
  }

  if (Array.isArray(data)) return data

  if (typeof data !== 'object' || data === null) return []

  const tryArray = (obj: Record<string, unknown>): unknown[] | null => {
    const keys = [
      'risks',
      'data',
      'items',
      'rows',
      'result',
      'extractedRisks',
      'extracted_risks',
      'detectedRisks',
      'detected_risks',
      'risques',
    ]
    for (const k of keys) {
      const v = obj[k]
      if (Array.isArray(v)) return v
    }
    return null
  }

  const top = tryArray(data as Record<string, unknown>)
  if (top) return top

  const inner = (data as Record<string, unknown>).data
  if (inner && typeof inner === 'object' && inner !== null) {
    const nested = tryArray(inner as Record<string, unknown>)
    if (nested) return nested
    if (Array.isArray(inner)) return inner
  }

  const payload = (data as Record<string, unknown>).payload
  if (payload && typeof payload === 'object' && payload !== null) {
    const fromPayload = tryArray(payload as Record<string, unknown>)
    if (fromPayload) return fromPayload
    const pData = (payload as Record<string, unknown>).data
    if (Array.isArray(pData)) return pData
  }

  return []
}
